import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { Table, Card as CardAnt, Typography } from 'antd';

import {
  ColSize,
  ContentSection,
  Form,
  InputForm,
  RowSize,
  SelectForm,
} from '../../../../../../../../../../../../../shared/components';
import {
  ObjectFormSection,
  PLAN_TYPE
} from '../../../../../../../../../shared';

import { GenericDataFormContext, QuotationInfoContext } from '../../../../../../../../../shared/contexts';
import { StepFourFormContext } from '../../../../contexts/StepFourForm';
import { StepThreeFormContext } from '../../../../../../../StepThree/contexts/StepThreeForm';
import { StepTwoFormContext } from '../../../../../../../StepTwo/contexts/StepTwoForm/StepTwoFormContext';

import { HomeQuotationComponent } from '../../../../../../../../../../../../../shared/interfaces';

import {
  formattedPrice,
  ValidateForm,
} from '../../../../../../../../../../../../../shared/utils';

import './index.css';
import { COLORS } from '../../../../../../../../../../../../../config/themes/colors';
import { TransferRightsForm } from '../../../TransferRightsForm';

interface FormCoveragesProps {
  homeComponent: HomeQuotationComponent;
}

const MOCK_TABLE_COLUMNS_HEADER = [
  {
    title: 'Descripción de cobertura',
    dataIndex: 'description',
    width: '50%',
  },
  {
    title: 'Suma asegurada',
    dataIndex: 'sumInsured',
    width: '50%',
  },
];

interface Coverage {
  codigo: string;
  description: string;
  familyCode?: string;
  familyDescription?: string;
  key: string;
  riskCode: string;
  sumInsured: string;
  typeCoverage?: string;
}

interface CoverageGroup {
  [familyCode: string]: Coverage[];
}

const hasAddressFromService = (address?: string): boolean => {
  if (!address) return false;

  const trimmed = address.trim();
  if (!trimmed) return false;

  const lowered = trimmed.toLowerCase();
  return lowered !== 'undefined' && lowered !== 'null';
};

export const FormCoverages: FC<FormCoveragesProps> = ({ homeComponent }) => {
  const validateForm = new ValidateForm();

  const { propertyDataForm } = useContext(StepFourFormContext);
  const { provinceList, allLocalitiesPerProvince, allCoveragesPerPlan } = useContext(
    GenericDataFormContext
  );
  const {
    stepTwoForm,
    planOptionsBySelectedPlan,
    planTypeSelected,
    housingTypeOptionsPerPlan
  } = useContext(StepTwoFormContext);

  const { planSelectedSummary } = useContext(StepThreeFormContext);

  const [coveragesListInfo, setCoveragesListInfo] = useState<CoverageGroup[]>(
    []
  );

  const provinceValueByTab = stepTwoForm?.getFieldValue(
    `province-tab-${homeComponent.nroComponente}`
  );

  const locationValueByTab = stepTwoForm?.getFieldValue(
    `location-tab-${homeComponent.nroComponente}`
  );

  const typeHousingValueByTab = stepTwoForm?.getFieldValue(
    `type-housing-tab-${homeComponent.nroComponente}`
  );

  const { renewalQuotationState } = useContext(QuotationInfoContext);
  const isRenewal = !!renewalQuotationState;

  const propertyDataAddressValueByTab = stepTwoForm?.getFieldValue(`propertyDataAddress${homeComponent.nroComponente}`);
  const shouldLockAddressInput = hasAddressFromService(propertyDataAddressValueByTab);

  const planTypeValueByTab =
    planTypeSelected === PLAN_TYPE.OPEN
      ? stepTwoForm?.getFieldValue(`plan-tab-${homeComponent.nroComponente}`)
      : planSelectedSummary;

  const planSelected = stepTwoForm?.getFieldValue(
    `plan-tab-${homeComponent.nroComponente}`
  );

  const housingTypesOptions = useMemo(
    () => housingTypeOptionsPerPlan.find(
      el => String(el.plan) === String(planSelected)
    )?.housingTypes ?? [], 
    [housingTypeOptionsPerPlan, planSelected]
  );
  
  const localitiesPerProvince = allLocalitiesPerProvince.find(
    (loc) => String(loc.provinceCode) === String(provinceValueByTab)
  )?.localitiesOptions;

  const setValuesForm = () => {
    propertyDataForm.setFieldValue(
      `propertyDataAddress${homeComponent.nroComponente}`,
      propertyDataAddressValueByTab
    );
    propertyDataForm.setFieldValue(
      `propertyDataProvince${homeComponent.nroComponente}`,
      provinceValueByTab
    );
    propertyDataForm.setFieldValue(
      `propertyDataLocality${homeComponent.nroComponente}`,
      locationValueByTab
    );
    propertyDataForm.setFieldValue(
      `typeHousing${homeComponent.nroComponente}`,
      typeHousingValueByTab
    );
    propertyDataForm.setFieldValue(
      `planSelectedEmission${homeComponent.nroComponente}`,
      planTypeValueByTab
    );
  };

  useEffect(() => {
    setValuesForm();
  }, [homeComponent]);

  const setCompleteCoveragesListInfoClosed = useCallback(() => {

    const coveragesPlanSelected = allCoveragesPerPlan.find((el => ( el.plan === String(homeComponent.codPlan))));

    if (coveragesPlanSelected) {
      const filteredCoverages = coveragesPlanSelected.coverages.filter(
        (planCoverage) =>
          homeComponent.cobertura.some(
            (cov: any) => cov.cobertura === planCoverage.codigo
          )
      );

      const coveragesListInfo = filteredCoverages.map((planCoverage) => {
        const matchedCoverage = homeComponent.cobertura.find(
          (cov) => cov.cobertura === planCoverage.codigo
        );

        let familyDescription;

        if (planCoverage?.codigoFamilia) {
          familyDescription = planCoverage.descripcionFamilia;
        } else if (planCoverage.tipoCobertura === 'O') {
          familyDescription = 'ADICIONAL';
        } else {
          familyDescription = 'OTRAS';
        }

        return {
          codigo: planCoverage.codigo,
          description: planCoverage.descripcionLarga,
          familyCode:
            planCoverage?.codigoFamilia ??
            (planCoverage.tipoCobertura === 'O'
              ? 'additional'
              : 'noFamilyCode'),
          familyDescription,
          key: planCoverage.codigo,
          riskCode: planCoverage.codigoRiesgo,
          sumInsured: `$ ${formattedPrice(
            `${matchedCoverage ? matchedCoverage.sumaAsegurada : 0}`
          )}`,
          typeCoverage: planCoverage?.tipoCobertura,
        };
      });

      const groupedData = coveragesListInfo.reduce<{
        [key: string]: Coverage[];
      }>((acc: any, item: any) => {
        let codeFamilyData: string;

        codeFamilyData = item.familyCode;

        if (!acc[codeFamilyData]) acc[codeFamilyData] = [];

        acc[codeFamilyData].push(item);
        return acc;
      }, {});

      const desiredOrder = ['noFamilyCode', 'additional'];
      const orderedGroupedData: { [key: string]: Coverage[] } = {};

      desiredOrder.forEach((key) => {
        if (groupedData[key]) orderedGroupedData[key] = groupedData[key];
      });

      Object.keys(groupedData)
        .filter((key) => !desiredOrder.includes(key))
        .forEach((key) => {
          orderedGroupedData[key] = groupedData[key];
        });

      setCoveragesListInfo([orderedGroupedData]);

      return;
    }

    setCoveragesListInfo([]);
  }, [allCoveragesPerPlan]);

  const setCompleteCoveragesListInfo = useCallback(() => {
      
    const matchingCoveragesByTab = allCoveragesPerPlan?.find(
      (el) => String(el.plan as string) === String(planSelected)
    );

    if (matchingCoveragesByTab) {
      const filteredCoverages = matchingCoveragesByTab.coverages.filter(
        (planCoverage) =>
          homeComponent.cobertura.some(
            (cov: any) => cov.cobertura === planCoverage.codigo
          )
      );

      const coveragesListInfo = filteredCoverages.map((planCoverage) => {
        const matchedCoverage = homeComponent.cobertura.find(
          (cov) => cov.cobertura === planCoverage.codigo
        );

        let familyDescription;

        if (planCoverage?.codigoFamilia) {
          familyDescription = planCoverage.descripcionFamilia;
        } else if (planCoverage.tipoDeCobertura === 'O') {
          familyDescription = 'ADICIONAL';
        } else {
          familyDescription = 'OTRAS';
        }

        return {
          codigo: planCoverage.codigo,
          description: planCoverage.descripcionLarga,
          familyCode:
            planCoverage?.codigoFamilia ??
            (planCoverage.tipoDeCobertura === 'O'
              ? 'additional'
              : 'noFamilyCode'),
          familyDescription,
          key: planCoverage.codigo,
          riskCode: planCoverage.codigoRiesgo,
          sumInsured: `$ ${formattedPrice(
            `${matchedCoverage ? matchedCoverage.sumaAsegurada : 0}`
          )}`,
          typeCoverage: planCoverage?.tipoDeCobertura,
        };
      });

      const groupedData = coveragesListInfo.reduce<{
        [key: string]: Coverage[];
      }>((acc, item) => {
        let codeFamilyData: string;

        codeFamilyData = item.familyCode;

        if (!acc[codeFamilyData]) acc[codeFamilyData] = [];

        acc[codeFamilyData].push(item);
        return acc;
      }, {});

      const desiredOrder = ['noFamilyCode', 'additional'];
      const orderedGroupedData: { [key: string]: Coverage[] } = {};

      desiredOrder.forEach((key) => {
        if (groupedData[key]) orderedGroupedData[key] = groupedData[key];
      });

      Object.keys(groupedData)
        .filter((key) => !desiredOrder.includes(key))
        .forEach((key) => {
          orderedGroupedData[key] = groupedData[key];
        });

      setCoveragesListInfo([orderedGroupedData]);

      return;
    }

    setCoveragesListInfo([]);
  }, [allCoveragesPerPlan]);

  useEffect(() => {
    if (planTypeSelected === PLAN_TYPE.OPEN) {
      setCompleteCoveragesListInfo();

      return;
    }

    setCompleteCoveragesListInfoClosed();
  }, [setCompleteCoveragesListInfo, setCompleteCoveragesListInfoClosed]);

  return (
    <div className='formCoverages'>
      <CardAnt>
        <Form
          formProps={{
            form: propertyDataForm,
            name: `activity-form-${homeComponent.nroComponente}`,
          }}
        >
          <ContentSection level='h3' title='Datos del bien'>
            <RowSize>
              {planTypeSelected === PLAN_TYPE.OPEN && (
                <ColSize size={25}>
                  <SelectForm
                    formProps={{
                      label: 'Tipo de Vivienda:',
                      name: `typeHousing${homeComponent.nroComponente}`,
                    }}
                    selectProps={{
                      disabled: true,
                      options: housingTypesOptions,
                    }}
                  />
                </ColSize>
              )}

              <ColSize size={25}>
                <InputForm
                  formProps={{
                    label: 'Domicilio:',
                    name: `propertyDataAddress${homeComponent.nroComponente}`,
                    rules: validateForm.generateRules({
                      label: 'domicilio',
                      rules: ['required'],
                    }),
                    initialValue: isRenewal ? (propertyDataAddressValueByTab ?? '') : '',
                  }}
                  inputProps={{ placeholder: 'Senillosa 342 1B', disabled: shouldLockAddressInput }}
                />
              </ColSize>

              <ColSize size={25}>
                <SelectForm
                  formProps={{
                    label: 'Provincia:',
                    name: `propertyDataProvince${homeComponent.nroComponente}`,
                  }}
                  selectProps={{
                    disabled: true,
                    loading: provinceList?.length === 0,
                    options: provinceList,
                    showSearch: true,
                  }}
                />
              </ColSize>

              <ColSize size={25}>
                <SelectForm
                  formProps={{
                    label: 'Localidad:',
                    name: `propertyDataLocality${homeComponent.nroComponente}`,
                  }}
                  selectProps={{
                    disabled: true,
                    options: localitiesPerProvince,
                  }}
                />
              </ColSize>
            </RowSize>
          </ContentSection>

          <ContentSection level='h3' title='Plan elegido'>
            <RowSize>
              <ColSize size={25}>
                <SelectForm
                  formProps={{
                    label: 'Lista de Planes:',
                    name: `planSelectedEmission${homeComponent.nroComponente}`,
                  }}
                  selectProps={{
                    disabled: true,
                    options: planOptionsBySelectedPlan, //TODO SETEAR EL VALOR DEL PLAN ELEGIDO EN PASO 2 O 3 ACÁ
                  }}
                />
              </ColSize>
            </RowSize>
          </ContentSection>
        </Form>

        <ContentSection level='h3' title='Cobertura' hasCard={false}>
          {coveragesListInfo?.map((coverageGroup: any) => {
            const familyCodes = Object.keys(coverageGroup);

            return familyCodes.map((familyCode, index) => {
              const coverages = coverageGroup[familyCode];

              return (
                <React.Fragment key={`${familyCode}-${index}`}>
                  <ColSize size={100} className={`${index > 0 ? 'mt-2' : ''}`}>
                    <Typography.Title
                      level={5}
                      style={{
                        margin: 0,
                        marginBottom: 10,
                        color: COLORS.secondary[100],
                      }}
                    >
                      {coverages[0]?.familyDescription}
                    </Typography.Title>
                  </ColSize>
                  <Table
                    bordered
                    className='person-table'
                    columns={MOCK_TABLE_COLUMNS_HEADER}
                    dataSource={coverages}
                    pagination={false}
                    scroll={{ x: 'auto' }}
                  />
                </React.Fragment>
              );
            });
          })}
        </ContentSection>

        <ObjectFormSection
          coveragesListInfo={coveragesListInfo[0]}
          numberComponent={String(homeComponent.nroComponente)}
        />
        <TransferRightsForm homeComponent={homeComponent} />
      </CardAnt>
    </div>
  );
};
